import { addMonths, addWeeks } from "date-fns"
import { differenceInWeeksFloat, getStartOfCurrentWeekUTC } from "./datetime"
import { getForecastBetweenWeeks } from "./forecastUtils"

/**
 * Forecast-change metrics for the Forecast Updates page: how much the live forecast for the
 * next 1/3/6/12 months deviates from a historic forecast snapshot. One definition shared by
 * the table cells and the filter predicates so a filter can never disagree with its cell.
 */

export const startDate = getStartOfCurrentWeekUTC()

/**
 * The single source of truth for the four comparison ranges: the snapshot query payload, the
 * table columns, the filter fields and the filter predicates all derive from these rows, so a
 * range cannot exist on one surface and be missing or mispaired on another. `unitKey`/`pctKey`
 * double as colIds and filter field keys and persist in saved views — keep them stable.
 */
export const forecastChangeRanges = [
  {
    label: "OneMonth",
    name: "1 month",
    end: addMonths(startDate, 1),
    unitKey: "oneMonthChange",
    pctKey: "oneMonthChangePct",
  },
  {
    label: "ThreeMonths",
    name: "3 month",
    end: addMonths(startDate, 3),
    unitKey: "threeMonthChange",
    pctKey: "threeMonthChangePct",
  },
  {
    label: "SixMonths",
    name: "6 month",
    end: addMonths(startDate, 6),
    unitKey: "sixMonthChange",
    pctKey: "sixMonthChangePct",
  },
  {
    label: "OneYear",
    name: "12 month",
    end: addWeeks(startDate, 52),
    unitKey: "twelveMonthChange",
    pctKey: "twelveMonthChangePct",
  },
] as const

export type ForecastRangeLabel = (typeof forecastChangeRanges)[number]["label"]
export type ForecastChangeFieldKey = (typeof forecastChangeRanges)[number]["unitKey" | "pctKey"]

export const forecastRangeEnds = Object.fromEntries(
  forecastChangeRanges.map((r) => [r.label, r.end]),
) as Record<ForecastRangeLabel, Date>

/** Query payload for `getHistoricForecastRanges` — one `{ label, start, end }` window per range. */
export const forecastRangeOptions = forecastChangeRanges.map(({ label, end }) => ({
  label,
  start: startDate,
  end,
}))

// The current side of every comparison spans the same clean integer-week window the backend
// uses for the historic side (differenceInWeeksFloat, no +1 day). Both dates are fixed at
// module load, so the widths are too.
const rangeWindows = forecastChangeRanges.map(({ label, end }) => ({
  label,
  endWeek: differenceInWeeksFloat(startDate, end),
}))

/**
 * Net-resolved change vs. a historic forecast over one range. Both are `null` when the
 * snapshot has no value for the range (a zero snapshot counts as missing — there is nothing
 * to compare against) or while net factors load.
 */
export type RangeChange = { change: number | null; historic: number | null }

export type ForecastChanges = Record<ForecastRangeLabel, RangeChange>

/**
 * Change and comparison base per range for one variant. The current side comes from the live
 * forecast series so it reflects in-session edits.
 */
export function computeForecastChanges(
  ranges: { label: string; historicValue: number | null }[] | undefined,
  forecastArray: number[],
  toNet: (value: number) => number | null,
): ForecastChanges {
  const result = {} as ForecastChanges
  for (const { label, endWeek } of rangeWindows) {
    const historicValue = ranges?.find((x) => x.label === label)?.historicValue
    if (!historicValue) {
      result[label] = { change: null, historic: null }
      continue
    }
    const currentForecast = getForecastBetweenWeeks(forecastArray, 0, endWeek)
    result[label] = {
      change: toNet(currentForecast - historicValue),
      historic: toNet(historicValue),
    }
  }
  return result
}

/** Change as a percentage of the historic value, or `null` when there is nothing to compare. */
export function changePct(rangeChange: RangeChange | undefined): number | null {
  if (rangeChange == null) return null
  const { change, historic } = rangeChange
  if (change === null || historic === null || historic === 0) return null
  return (change / Math.max(historic, 1)) * 100
}
